import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  TrendingUp, 
  MousePointerClick, 
  Eye, 
  Euro,
  Target,
  Calendar,
  Users,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GoogleAdsDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'campaigns', 'accounts'

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch accounts, campaigns, and stats in parallel
      const [accountsRes, campaignsRes, statsRes] = await Promise.all([
        axios.get(`${API}/google-ads/accounts`, { headers }).catch(err => ({ data: { accounts: [] } })),
        axios.get(`${API}/google-ads/campaigns`, { headers }).catch(err => ({ data: { campaigns: [], summary: null } })),
        axios.get(`${API}/google-ads/stats?days=30`, { headers }).catch(err => ({ data: { daily_stats: [] } }))
      ]);
      
      setAccounts(accountsRes.data.accounts || []);
      setCampaigns(campaignsRes.data.campaigns || []);
      setSummary(campaignsRes.data.summary || null);
      setStats(statsRes.data.daily_stats || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAccounts = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/google-ads/sync-accounts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      console.error('Error syncing accounts:', error);
      toast.error('Fehler beim Synchronisieren der Accounts');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCampaigns = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/google-ads/sync-campaigns`,
        { days_back: 30 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show detailed success message with ad count
      const message = response.data.message || 
        `${response.data.total_campaigns} Kampagnen und ${response.data.total_ads || 0} Anzeigen synchronisiert`;
      toast.success(message);
      fetchAllData();
    } catch (error) {
      console.error('Error syncing campaigns:', error);
      if (error.response?.status === 404) {
        toast.error('Bitte zuerst Accounts synchronisieren');
      } else {
        toast.error('Fehler beim Synchronisieren der Kampagnen');
      }
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('de-DE').format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Google Ads Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Google Ads Analytics</h1>
              <p className="mt-1 text-sm text-gray-500">
                Kampagnenstatistiken aus MCC Account 533-828-9654
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Zurück zum Admin
              </button>
              <button
                onClick={handleSyncAccounts}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Accounts
              </button>
              <button
                onClick={handleSyncCampaigns}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-[#0040FF] text-white rounded-lg hover:bg-[#0035dd] transition disabled:opacity-50 disabled:cursor-not-allowed"
                title="Synchronisiert Kampagnen, Statistiken und Anzeigentexte"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                Kampagnen & Anzeigen
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['overview', 'campaigns', 'accounts'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    py-4 px-1 border-b-2 font-medium text-sm transition
                    ${activeTab === tab 
                      ? 'border-[#0040FF] text-[#0040FF]' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab === 'overview' && 'Übersicht'}
                  {tab === 'campaigns' && `Kampagnen (${campaigns.length})`}
                  {tab === 'accounts' && `Accounts (${accounts.length})`}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Impressions</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatNumber(summary.total_impressions)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-full p-3">
                      <Eye className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-sm">
                    <span className="text-gray-500">Letzte 30 Tage</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Clicks</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatNumber(summary.total_clicks)}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-full p-3">
                      <MousePointerClick className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-sm">
                    <span className="text-gray-500">CTR: {summary.average_ctr}%</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Kosten</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(summary.total_cost)}
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-full p-3">
                      <Euro className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-sm">
                    <span className="text-gray-500">CPC: {formatCurrency(summary.average_cpc)}</span>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Conversions</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatNumber(summary.total_conversions)}
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-full p-3">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-sm">
                    <span className="text-gray-500">Letzte 30 Tage</span>
                  </div>
                </div>
              </div>
            )}

            {/* Daily Stats Chart */}
            {stats.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance (Letzte 30 Tage)</h2>
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Kosten</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conversions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {stats.slice(0, 10).map((stat, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(stat.date).toLocaleDateString('de-DE')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(stat.impressions)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(stat.clicks)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatCurrency(stat.cost)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatNumber(stat.conversions)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'campaigns' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Alle Kampagnen</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kampagne</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CTR</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CPC</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Kosten</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm">
                          <div className="font-medium text-gray-900">{campaign.campaign_name}</div>
                          <div className="text-gray-500 text-xs">{campaign.advertising_channel_type}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{campaign.account_name}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            campaign.status === 'ENABLED' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {formatNumber(campaign.total_impressions)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {formatNumber(campaign.total_clicks)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {campaign.ctr}%
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right">
                          {formatCurrency(campaign.avg_cpc)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                          {formatCurrency(campaign.total_cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accounts' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Google Ads Accounts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accounts.map((account) => (
                  <div key={account.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{account.descriptive_name}</h3>
                        <p className="text-sm text-gray-500 mt-1">ID: {account.customer_id}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        account.is_manager 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {account.is_manager ? 'Manager' : 'Client'}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Währung:</span> {account.currency_code}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Zeitzone:</span> {account.time_zone}
                      </p>
                      {account.last_synced_at && (
                        <p className="text-xs text-gray-500">
                          Sync: {new Date(account.last_synced_at).toLocaleString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleAdsDashboard;
